@if (formGroup) {
  <div class="rentgeneral-header">
    <span class="section-title">Lignes de location</span>
    @if (planningVisible) {
      <button type="button" mat-stroked-button class="planning-btn" matTooltip="Planning de facturation du dossier" (click)="openPlanningDialog()">
        <mat-icon>view_timeline</mat-icon>
        Planning
      </button>
    }
  </div>

  @if (!closedLinesLoaded && formGroup.get('id')?.value > 0) {
    <div class="returned-toggle">
      Matériels repris non chargés · <a href="" (click)="$event.preventDefault(); loadClosedLines.emit()">Les charger</a>
    </div>
  } @else if (hiddenReturnedCount) {
    <div class="returned-toggle">
      @if (hideReturned) {
        {{ hiddenReturnedCount }} ligne{{ hiddenReturnedCount > 1 ? 's' : '' }} reprise{{ hiddenReturnedCount > 1 ? 's' : '' }} masquée{{ hiddenReturnedCount > 1 ? 's' : '' }}
        · <a href="" (click)="$event.preventDefault(); hideReturned = false">Tout afficher</a>
      } @else {
        <a href="" (click)="$event.preventDefault(); hideReturned = true">Masquer les {{ hiddenReturnedCount }} lignes reprises</a>
      }
    </div>
  }

  <form [formGroup]="formGroup">
    @if (rentLines.length) {
      <table class="rent-table">
        <thead>
          <tr class="tablerow">
            <th class="coldir"></th>
            <th class="colseq">#</th>
            <th class="colid">Id</th>
            <th class="colproductname">Produit</th>
            <th class="colcontract">Contrat</th>
            <th class="colperiod">Périodicité</th>
            <th class="colduration" matTooltip="Durée totale prévue par la prescription (en unités de période)">Durée</th>
            <th class="colqty" matTooltip="Quantité de l'article loué">Qté</th>
            <th class="colprice" [class.ht-background]="is_ht" [class.ttc-background]="!is_ht">
              <div>Prix</div>
              <div [class.text-error]="is_ht" [class.text-success]="!is_ht">{{ is_ht ? 'HT €' : 'TTC €' }}</div>
            </th>
            <th class="collpp">LPP</th>
            <th class="colstate">État</th>
            <th class="coldelete"></th>
          </tr>
        </thead>
        <tbody>
          <!-- Suivi par contrôle et non par position : après une suppression, une ligne ne reprend pas la couleur (en fondu de 1,5 s) de sa voisine. -->
          @for (item of shownRentLines; track item.control) {
            <tr class="tablerow" [class]="lineClass(item.control)">
              <td class="coldir"><app-rental-direction [typ]="item.control.get('typ')?.value" [qty]="item.control.get('qty')?.value"></app-rental-direction></td>
              <td class="colseq">
                <div class="seq-container">
                  <span class="seq-num">{{ item.control.get('seq')?.value }}</span>
                </div>
              </td>
              <td class="colid">{{ item.control.get('product')?.value }}</td>
              <td class="colproductname">
                <div class="flexcolumn">
                  <div class="flexrow" style="justify-content: center">
                    @if (is_configurable_line(item.control)) {
                      <button mat-icon-button color="primary" matTooltip="Configurer" aria-label="Configurer" (click)="onConfiguration(item.control)" style="margin-right: 6px">
                        <mat-icon style="font-size: 15px">settings</mat-icon>
                      </button>
                    }
                    <!-- Nouvel onglet : la location en cours reste dans celui-ci (JP, 26/10). -->
                    <a class="primary" [routerLink]="['/sales/product', $safeNavigationMigration(item.control.get('product')?.value)]" target="_blank">
                      {{ item.control.get('product_name')?.value }}
                    </a>
                  </div>
                  @if (is_configurable_line(item.control)) {
                    <div class="config-block">
                      <div class="config-title">
                        {{ item.control.value.data.cufs[C_CUF_CONFIGURATION].split('.').pop() }}
                      </div>
                      @for (conf of item.control.value.data.cufs[C_CUF_CONFIGURATION_VALUE]; track conf.seq) {
                        <div class="config-item">{{ conf.order_node_name }}</div>
                      }
                    </div>
                  }
                  <!-- Mémo de ligne (sales_order_l.memo) : saisi ici seulement, imprimé sous la désignation du BL ; figé comme la ligne hors CREATED. -->
                  @if (isLineEditable) {
                    <textarea class="line-memo-input" cdkTextareaAutosize cdkAutosizeMinRows="1" aria-label="Mémo de la ligne" placeholder="Mémo (imprimé sur le BL)" [formControl]="$any(item.control.get('memo'))" (blur)="trimMemo(item.control)"></textarea>
                  } @else if (item.control.get('memo')?.value) {
                    <div class="line-memo">{{ item.control.get('memo')?.value }}</div>
                  }
                  <!-- Lien avec le bon de livraison (BL) associé -->
                  @if (getDeliveryForLine(item.control); as del) {
                    <div class="line-delivery-info">
                      <mat-icon class="del-icon">local_shipping</mat-icon>
                      <span>
                        BL #{{ del.id }}
                        @if (del.realdatdel) {
                          &bull; Livré le {{ del.realdatdel | date:'dd/MM/yyyy' }}
                        } @else if (del.plandatdel) {
                          &bull; Prévu le {{ del.plandatdel | date:'dd/MM/yyyy' }}
                        }
                      </span>
                    </div>
                  }
                  <!-- BL de reprise : la ligne y est à −1 (règle de JP du 29/10) -->
                  @if (getReturnForLine(item.control); as ret) {
                    <div class="line-delivery-info">
                      <mat-icon class="del-icon">assignment_return</mat-icon>
                      <span>
                        BL #{{ ret.id }}
                        @if (ret.realdatdel) {
                          &bull; Repris le {{ ret.realdatdel | date:'dd/MM/yyyy' }}
                        } @else if (ret.plandatdel) {
                          &bull; Reprise prévue le {{ ret.plandatdel | date:'dd/MM/yyyy' }}
                        }
                      </span>
                    </div>
                  }
                </div>
              </td>
              <td class="colcontract">{{ item.control.get('rental_terms_snapshot')?.value?.name ?? item.control.get('rental_terms')?.value?.lnk_rental_terms?.name }}</td>
              <td class="colperiod">{{ periodLabel($safeNavigationMigration(item.control.get('rental_terms_snapshot')?.value?.period ?? item.control.get('rental_terms')?.value?.lnk_rental_terms?.period)) }}</td>
              <td class="colduration">
                @if (!isReprise(item.control)) {
                  @if (isLineEditable) {
                    <input type="number" min="1" step="1" class="qty-input" [formControl]="$any(getDurationControl(item.control))" />
                  } @else {
                    <span>{{ (item.control.get('rental_lifecycle')?.value?.qty ?? 0) > 0 ? item.control.get('rental_lifecycle')?.value?.qty : '∞' }}</span>
                  }
                }
              </td>
              <!-- Ligne louée = un matériel (quantité 1, −1 hors contrat) : le serveur refuse toute autre quantité depuis le 31/10. -->
              <td class="colqty">
                @if (isLineEditable && item.control.get('typ')?.value !== 'RENTAL') {
                  <input type="number" min="1" step="1" class="qty-input" [formControl]="$any(item.control.get('qty'))" />
                } @else {
                  <span>{{ item.control.get('qty')?.value }}</span>
                }
              </td>

              @let tiered = $safeNavigationMigration(item.control.get('rental_terms_snapshot')?.value?.tiered_pricing ?? item.control.get('rental_terms')?.value?.lnk_rental_terms?.tiered_pricing);
              @let tp1 = $safeNavigationMigration(tiered?.[0]);
              @let tp2 = $safeNavigationMigration(tiered?.[1]);

              <td class="colprice">
                <div class="tiered-stack">
                  <div class="tiered-row">
                    @if (tp2) {
                      <span class="period-tag" [matTooltip]="'Période 1 à ' + (tp2.starting_period - 1)">P1</span>
                    }
                    @if (is_ht) {
                      <input class="price-input" [formControl]="$any(item.control.get('uprice_taxx'))" (input)="onPriceTaxxChange(item.control)" />
                    } @else {
                      <input class="price-input" [formControl]="$any(item.control.get('uprice_taxi'))" (input)="onPriceTaxiChange(item.control)" />
                    }
                  </div>
                  @if (tp2) {
                    <div class="tiered-row secondary">
                      <span class="period-tag" [matTooltip]="'À partir de la période ' + tp2.starting_period">P2</span>
                      @if (is_ht) {
                        <input class="price-input" [value]="tp2.uprice_taxx" (input)="onTp2PriceTaxxChange(tp2, item.control, $any($event.target).value)" />
                      } @else {
                        <input class="price-input" [value]="calcTtc(tp2.uprice_taxx, item.control.get('vat_rate')?.value)" (input)="onTp2PriceTaxiChange(tp2, item.control, $any($event.target).value)" />
                      }
                    </div>
                  }
                </div>
              </td>

              <td class="collpp">
                <div class="tiered-stack center">
                  <div class="tiered-row center">
                    @if (tp2) {
                      <span class="period-tag">P1</span>
                    }
                    <span>{{ tp1?.fse_lpp ? tp1.fse_lpp + (tp1.tarifref != null ? ' (' + (tp1.tarifref | fc) + ' €)' : '') : '—' }}</span>
                  </div>
                  @if (tp2) {
                    <div class="tiered-row center secondary">
                      <span class="period-tag">P2</span>
                      <span>{{ tp2.fse_lpp ? tp2.fse_lpp + (tp2.tarifref != null ? ' (' + (tp2.tarifref | fc) + ' €)' : '') : '—' }}</span>
                    </div>
                  }
                </div>
              </td>
              <td class="colstate">
                <div class="state-container">
                  @if (isReprise(item.control)) {
                    @if (getReturnForLine(item.control)?.realdatdel) {
                      <span class="rental-state state-5" [matTooltip]="'Récupéré le ' + (getReturnForLine(item.control)?.realdatdel | date:'dd/MM/yyyy')">
                        Récupéré
                      </span>
                    } @else {
                      <span class="rental-state state-return-pending" matTooltip="Reprise programmée / En attente de récupération">
                        À récupérer
                      </span>
                    }
                  } @else if (item.control.get('rental_lifecycle')?.value?.return_realdatdel) {
                    <span class="rental-state state-5" [matTooltip]="'Restitué le ' + (item.control.get('rental_lifecycle')?.value?.return_realdatdel | date:'dd/MM/yyyy')">
                      Restitué
                    </span>
                  } @else if (getReturnForLine(item.control); as ret) {
                    <span class="rental-state state-return-pending" [matTooltip]="'Reprise programmée sur le BL ' + ret.id">
                      À récupérer
                    </span>
                  } @else if (hasActiveSuspension(item.control)) {
                    <span class="rental-state state-suspended" [matTooltip]="getSuspensionTooltip(item.control)">
                      ⏸️ Suspendu
                    </span>
                  } @else {
                    <span [class]="'rental-state state-' + (item.control.get('rental_lifecycle')?.value?.state ?? item.control.get('rental_terms')?.value?.state ?? 0)">
                      {{ rentalStateLabel($safeNavigationMigration(item.control.get('rental_lifecycle')?.value?.state ?? item.control.get('rental_terms')?.value?.state ?? 0)) }}
                    </span>
                  }

                  @if (!isReprise(item.control)) {
                    <button
                      type="button"
                      class="custom-icon-button pause-btn"
                      [class.active-pause]="hasActiveSuspension(item.control)"
                      matTooltip="Gérer les suspensions (hospitalisation, etc.)"
                      (click)="openOffHireDialog(item.control)"
                    >
                      <mat-icon style="font-size: 15px; width: 15px; height: 15px">pause_circle_outline</mat-icon>
                    </button>
                  }
                </div>
              </td>
              <td class="coldelete">
                @if (formGroup.value.state === SalesOrderState.CREATED) {
                  <button type="button" class="custom-icon-button error" matTooltip="Supprimer" (click)="deleteLine(item.index)">
                    <mat-icon style="font-size: 16px; max-height: 20px; max-width: 20px">delete</mat-icon>
                  </button>
                }
              </td>
            </tr>
          }
        </tbody>
      </table>
    }
  </form>

  @if (!rentLines.length) {
    <div class="nolines">Aucune ligne de location</div>
  }

  @if (isLineEditable) {
    <div class="inputzone bglavander">
      <smart-select [items]="lineTypes" bindValue="value" bindLabel="label" height="29px" optionHeight="32px" width="220px" [formControl]="linetype"> </smart-select>
      <app-search-product tabindex="2" #searchProduct width="510px" (blurEmitter)="blurEmitterSearchProduct($event)" (selectProductEmitter)="selectProductEmitter($event)" [is_ht]="is_ht" [disabled]="!linetype.value"> </app-search-product>
    </div>

    @if (showRentalTermsSelection && availableRentalTerms.length) {
      <div class="rental-selection-panel">
        <div class="rental-selection-header">
          <span
            >Contrats disponibles pour <strong>{{ selectedProduct?.name }}</strong></span
          >
          <button type="button" class="custom-icon-button" matTooltip="Annuler" (click)="cancelSelection()">
            <mat-icon>close</mat-icon>
          </button>
        </div>
        <div class="rental-terms-list">
          @for (rt of availableRentalTerms; track rt.id) {
            <div class="rental-term-card" [class.has-error]="isRentalTermInError(rt)" (click)="selectRentalTerm(rt)" [matTooltip]="isRentalTermInError(rt) ? 'Contrat en anomalie : sélection impossible' : 'Cliquer pour ajouter ce contrat'">
              <div class="rt-name">{{ rt.name }}</div>
              @if (isRentalTermInError(rt)) {
                <div class="rt-error-badge">
                  <mat-icon style="font-size: 14px; height: 14px; width: 14px; vertical-align: middle">error_outline</mat-icon>
                  <span>{{ rt.error || 'Tarif de référence LPP introuvable ou expiré' }}</span>
                </div>
              }
              <div class="rt-details">
                <span class="rt-period">{{ periodLabel(rt.period) }}</span>
                @if (rt.tiered_pricing.length) {
                  <span class="rt-price">{{ rt.tiered_pricing[0].uprice_taxx | fc }} € HT</span>
                  <span class="rt-price">{{ calcTtc(rt.tiered_pricing[0].uprice_taxx, rt.vat_rate) | fc }} € TTC</span>
                }
              </div>
              @if (rt.tiered_pricing.length && rt.tiered_pricing[0].fse_lpp) {
                <div class="rt-lpp">
                  LPP: {{ rt.tiered_pricing[0].fse_lpp }}
                  @if (rt.tiered_pricing[0].tarifref != null) {
                    ({{ rt.tiered_pricing[0].tarifref | fc }} €)
                  }
                </div>
              }
            </div>
          }
        </div>
      </div>
    }
  }
}
